import { ChangeDetectionStrategy, Component, computed, input, InputSignal, output, OutputEmitterRef, Signal } from '@angular/core';
import { ChipControlGroupComponent } from '@libs/form/components';
import type { InputItemModel } from '@libs/form/models';
import {
  OrderHistoryItemAssortment,
  OrderHistoryItemAvailability,
  type OrderHistoryItemAvailabilityType,
  OrderHistoryItemDeliveryDate,
  type OrderHistoryItemModel,
  OrderHistoryItemSortBy,
  type OrderHistoryItemSortByValueType,
  type OrderHistoryRecordFilter,
} from '@libs/services/models';
import type { TopLevelCategoryNode } from '@libs/shop/models';
import { DateTime } from 'luxon';
import { isSameFilterDate } from '../../order-history-filter.util';
import type { OrderHistoryRecordFilterFormModel } from './order-history-record-filter.model';

@Component({
  selector: 'ui-order-history-record-filter',
  templateUrl: './order-history-record-filter.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ChipControlGroupComponent],
})
export class OrderHistoryRecordFilterComponent {
  public nodes: InputSignal<TopLevelCategoryNode<OrderHistoryItemModel>[]> = input.required();
  public filter: InputSignal<OrderHistoryRecordFilter> = input.required();
  public sort: InputSignal<OrderHistoryItemSortByValueType> = input.required();

  public filterChange: OutputEmitterRef<OrderHistoryRecordFilter> = output();
  public sortChange: OutputEmitterRef<OrderHistoryItemSortByValueType> = output();

  public inputItems: Signal<InputItemModel[]> = computed(() => this.mapInputItems());

  public handleValueChange(value: unknown): void {
    const parsed: OrderHistoryRecordFilterFormModel = value as OrderHistoryRecordFilterFormModel;

    const from: DateTime | undefined = parsed.deliveryDate?.start?.isValid ? parsed.deliveryDate.start : undefined;
    const to: DateTime | undefined = parsed.deliveryDate?.end?.isValid ? parsed.deliveryDate.end : undefined;

    const filterResult: OrderHistoryRecordFilter = {
      categoryIds: Object.entries(parsed.assortment ?? {}).filter(([, selected]) => selected).map(([id]) => id),
      availability: parsed.availability ? parsed.availability as OrderHistoryItemAvailabilityType : undefined,
      delivery: from || to ? { from, to } : undefined,
    };

    // the group emits the whole form on any change, so only emit the output that actually changed (e.g. changing the sort must not emit a filter).
    if (!this.isSameFilter(filterResult, this.filter())) {
      this.filterChange.emit(filterResult);
    }

    const sort: OrderHistoryItemSortByValueType = parsed.sort ?? 'position';

    if (sort !== this.sort()) {
      this.sortChange.emit(sort);
    }
  }

  private isSameFilter(a: OrderHistoryRecordFilter, b: OrderHistoryRecordFilter): boolean {
    return a.availability === b.availability
      && a.categoryIds.length === b.categoryIds.length
      && a.categoryIds.every(id => b.categoryIds.includes(id))
      && isSameFilterDate(a.delivery?.from, b.delivery?.from)
      && isSameFilterDate(a.delivery?.to, b.delivery?.to);
  }

  private mapInputItems(): InputItemModel[] {
    const filter: OrderHistoryRecordFilter = this.filter();

    return [
      { ...OrderHistoryItemSortBy, value: this.sort() },
      {
        ...OrderHistoryItemAssortment,
        options: this.nodes().map(node => ({ name: node.id, label: `${node.label} (${node.count})` })),
        value: Object.fromEntries(this.nodes().map(node => [node.id, filter.categoryIds.includes(node.id)])),
      },
      { ...OrderHistoryItemAvailability, value: filter.availability ?? '' },
      {
        ...OrderHistoryItemDeliveryDate,
        value: { start: filter.delivery?.from ?? null, end: filter.delivery?.to ?? null },
      },
    ];
  }
}
